import { useLocation, useNavigate } from "react-router-dom";
import { isMedicalRecordInternalTabNavigation } from "@/lib/medical-record-internal-tab-navigation";

type RouteMatcher = string | RegExp | ((pathname: string) => boolean);

export type SmartBackOptions = {
  fallbackRoute?: string;
  preferredRoute?: string;
  avoidRoutes?: RouteMatcher[];
};

const DEFAULT_FALLBACK_ROUTE = "/patients";

export function isEditRoute(pathname: string): boolean {
  return /\/edit(?:\/|$)/.test(pathname);
}

export function isCreationRoute(pathname: string): boolean {
  return (
    pathname === "/patients/new" ||
    pathname === "/patients/pets/new" ||
    /^\/patients\/pets\/[^/]+\/medical-records\/new(?:\/[^/]+)?$/.test(pathname)
  );
}

export function isInternalTabRoute(fromPathname: string, toPathname: string): boolean {
  if (isMedicalRecordInternalTabNavigation(fromPathname, toPathname)) return true;

  const fromPet = fromPathname.match(/^\/patients\/pets\/([^/]+)(?:\/(?!edit$|medical-records\/new).*)?$/);
  const toPet = toPathname.match(/^\/patients\/pets\/([^/]+)(?:\/(?!edit$|medical-records\/new).*)?$/);
  if (fromPet && toPet) return fromPet[1] === toPet[1];

  const fromOwner = fromPathname.match(/^\/patients\/owners\/([^/]+)(?:\/(?!edit$).*)?$/);
  const toOwner = toPathname.match(/^\/patients\/owners\/([^/]+)(?:\/(?!edit$).*)?$/);
  if (fromOwner && toOwner) return fromOwner[1] === toOwner[1];

  const tabRoutePatterns = [
    /^\/reports(?:\/.*)?$/,
    /^\/settings(?:\/(?!print\/).*)?$/,
    /^\/sales(?:\/[^/]+)?$/,
  ];

  return tabRoutePatterns.some((pattern) => pattern.test(fromPathname) && pattern.test(toPathname));
}

export function resolveMainBackRoute(currentPathname: string, fallbackRoute = DEFAULT_FALLBACK_ROUTE): string {
  const ownerEdit = currentPathname.match(/^\/patients\/owners\/([^/]+)\/edit$/);
  if (ownerEdit) return `/patients/owners/${ownerEdit[1]}`;

  const petEdit = currentPathname.match(/^\/patients\/pets\/([^/]+)\/edit$/);
  if (petEdit) return `/patients/pets/${petEdit[1]}`;

  const newMedicalRecord = currentPathname.match(/^\/patients\/pets\/([^/]+)\/medical-records\/new(?:\/[^/]+)?$/);
  if (newMedicalRecord) return fallbackRoute;

  if (isCreationRoute(currentPathname)) return fallbackRoute;

  if (isEditRoute(currentPathname)) return fallbackRoute;

  return fallbackRoute;
}

function matchesRoute(pathname: string, matcher: RouteMatcher): boolean {
  if (typeof matcher === "string") return pathname === matcher || pathname.startsWith(`${matcher}/`);
  if (matcher instanceof RegExp) return matcher.test(pathname);
  return matcher(pathname);
}

function normalizeSmartBackOptions(input?: string | SmartBackOptions): Required<Pick<SmartBackOptions, "fallbackRoute" | "avoidRoutes">> &
  Pick<SmartBackOptions, "preferredRoute"> {
  if (typeof input === "string") {
    return { fallbackRoute: input, avoidRoutes: [], preferredRoute: undefined };
  }

  return {
    fallbackRoute: input?.fallbackRoute ?? DEFAULT_FALLBACK_ROUTE,
    preferredRoute: input?.preferredRoute,
    avoidRoutes: input?.avoidRoutes ?? [],
  };
}

export function resolveSmartBackRoute(currentPathname: string, input?: string | SmartBackOptions): string | null {
  const { fallbackRoute, preferredRoute, avoidRoutes } = normalizeSmartBackOptions(input);

  if (preferredRoute) return preferredRoute;

  if (
    isEditRoute(currentPathname) ||
    isCreationRoute(currentPathname) ||
    avoidRoutes.some((matcher) => matchesRoute(currentPathname, matcher))
  ) {
    return resolveMainBackRoute(currentPathname, fallbackRoute);
  }

  return null;
}

/**
 * Smart back navigation hook that behaves like browser back when possible.
 *
 * - If a preferred route is provided, it navigates there directly.
 * - Edit/create routes resolve to their main fallback instead of stepping through
 *   transient history entries.
 * - Otherwise, if there is navigation history inside the SPA, it goes back 1 entry.
 * - If there's no back history, it navigates to the fallback route.
 *
 * @returns A function that accepts an optional fallbackRoute string or options object
 *
 * @example
 * ```tsx
 * const smartBack = useSmartBack();
 *
 * <Button onClick={() => smartBack()}>Back</Button>
 * // Or with custom fallback:
 * <Button onClick={() => smartBack("/custom/route")}>Back</Button>
 * ```
 */
export function useSmartBack() {
  const navigate = useNavigate();
  const location = useLocation();

  return (input?: string | SmartBackOptions) => {
    const { fallbackRoute } = normalizeSmartBackOptions(input);
    const currentPathname = location.pathname;
    const semanticBackRoute = resolveSmartBackRoute(currentPathname, input);

    if (semanticBackRoute) {
      navigate(semanticBackRoute, { replace: true });
      return;
    }

    const canGoBack = (() => {
      if (typeof window === "undefined") return false;

      const historyState = window.history.state as unknown;
      if (historyState && typeof historyState === "object" && "idx" in historyState) {
        const idx = (historyState as { idx?: unknown }).idx;
        if (typeof idx === "number") return idx > 0;
      }

      return window.history.length > 1;
    })();

    if (canGoBack) {
      navigate(-1);
      return;
    }

    navigate(fallbackRoute, { replace: true });
  };
}
